import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { 
  Upload, 
  FileSpreadsheet, 
  AlertCircle, 
  CheckCircle2, 
  X,
  Lightbulb,
  FileText,
  Calendar,
  Download,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import type { RoleBudget } from '@/types/budget.types';

export interface ImportValidationError {
  row: number;
  column?: string;
  field: string;
  value: string;
  error: string;
  suggestion?: string;
}

export interface ImportPreview {
  year: number;
  roles: Array<{
    role: string;
    monthlyHours: number[];
    totalHours: number;
    isNew: boolean;
  }>;
  errors: ImportValidationError[];
  warnings: string[];
}

interface ImportBudgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (data: ImportPreview, replaceMode: boolean) => Promise<void>;
  existingRoles: RoleBudget[];
  currentYear: number;
}

export function ImportBudgetDialog({
  open,
  onOpenChange,
  onImport,
  existingRoles,
  currentYear,
}: ImportBudgetDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    const { generateImportTemplate } = await import('@/lib/budget-export-utils');
    generateImportTemplate(currentYear);
  };

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setIsProcessing(true);
    setImportProgress(10);

    try {
      // Import the parsing utility dynamically
      const { parseImportFile } = await import('@/lib/budget-import-utils');
      
      setImportProgress(30);
      
      // Parse the file
      const result = await parseImportFile(selectedFile, existingRoles, currentYear);
      
      setImportProgress(70);
      
      setPreview(result);
      setImportProgress(100);
    } catch (error) {
      console.error('Failed to parse file:', error);
      setPreview({
        year: currentYear,
        roles: [],
        errors: [{
          row: 0,
          field: 'file',
          value: selectedFile.name,
          error: error instanceof Error ? error.message : 'Failed to parse file. Please check the file format.',
        }],
        warnings: [],
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!preview || preview.errors.length > 0) return;

    setIsProcessing(true);
    try {
      await onImport(preview, replaceMode);
      handleClose();
    } catch (error) {
      console.error('Import failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setReplaceMode(false);
    setImportProgress(0);
    onOpenChange(false);
  };

  const hasErrors = preview && preview.errors.length > 0;
  const hasWarnings = preview && preview.warnings.length > 0;
  const canImport = preview && !hasErrors && preview.roles.length > 0;

  const newRolesCount = preview?.roles.filter(r => r.isNew).length || 0;
  const updateRolesCount = preview?.roles.filter(r => !r.isNew).length || 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import Resource Budget
          </DialogTitle>
          <DialogDescription>
            Upload an Excel (.xlsx, .xls) or CSV file to import budget data. The system will validate your data and provide AI-powered suggestions for any errors.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload Section */}
          {!file && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm font-medium mb-2">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">
                  Excel (.xlsx, .xls) or CSV files
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0];
                    if (selectedFile) handleFileSelect(selectedFile);
                  }}
                />
              </div>

              {/* File Format Instructions */}
              <Alert>
                <AlertTitle>File Format Requirements</AlertTitle>
                <AlertDescription className="space-y-2 mt-2">
                  <p className="text-sm">Your file must include:</p>
                  <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                    <li>A <strong>"role"</strong> column with role names</li>
                    <li>At least one month column (e.g., "January", "Jan", "February", etc.)</li>
                    <li>Optional: Year information in the filename or as a column</li>
                    <li>All numeric values must be greater than 0</li>
                  </ul>
                  <p className="text-sm mt-2">
                    <strong>Multi-year support:</strong> Excel files with multiple sheets (one per year) will import all years automatically.
                  </p>
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadTemplate}
                      className="w-full"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Sample Template
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Processing Progress */}
          {isProcessing && (
            <div className="space-y-2">
              <Label>Processing file...</Label>
              <Progress value={importProgress} />
            </div>
          )}

          {/* Preview Section */}
          {file && preview && (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Year Information */}
              <Alert>
                <Calendar className="h-4 w-4" />
                <AlertTitle>Import Year</AlertTitle>
                <AlertDescription>
                  This data will be imported for year <strong>{preview.year}</strong>
                </AlertDescription>
              </Alert>

              {/* Errors */}
              {hasErrors && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Validation Errors ({preview.errors.length})</AlertTitle>
                  <AlertDescription>
                    <div className="mt-2 space-y-3 max-h-60 overflow-y-auto">
                      {preview.errors.map((error, index) => (
                        <div key={index} className="space-y-1 p-2 bg-background rounded border">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                Row {error.row}{error.column && `, Column: ${error.column}`}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {error.field}: "{error.value}"
                              </p>
                              <p className="text-sm mt-1">{error.error}</p>
                            </div>
                          </div>
                          {error.suggestion && (
                            <div className="flex items-start gap-2 mt-2 p-2 bg-primary/5 rounded border border-primary/20">
                              <Lightbulb className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-xs font-medium text-primary">AI Suggestion</p>
                                <p className="text-sm mt-0.5">Did you mean: <strong>{error.suggestion}</strong>?</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Warnings */}
              {hasWarnings && !hasErrors && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Warnings</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                      {preview.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Success Preview */}
              {!hasErrors && preview.roles.length > 0 && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertTitle>Import Preview</AlertTitle>
                  <AlertDescription>
                    <div className="space-y-3 mt-2">
                      <div className="flex gap-4">
                        {newRolesCount > 0 && (
                          <Badge variant="default">
                            {newRolesCount} new role{newRolesCount !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        {updateRolesCount > 0 && (
                          <Badge variant="secondary">
                            {updateRolesCount} role{updateRolesCount !== 1 ? 's' : ''} to update
                          </Badge>
                        )}
                      </div>

                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {preview.roles.map((role, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{role.role}</span>
                              {role.isNew && (
                                <Badge variant="outline" className="text-xs">New</Badge>
                              )}
                            </div>
                            <span className="text-muted-foreground">
                              {role.totalHours.toFixed(2)} hours
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Replace Mode Option */}
                      {updateRolesCount > 0 && (
                        <div className="flex items-start gap-2 p-3 bg-background rounded border">
                          <Checkbox
                            id="replace-mode"
                            checked={replaceMode}
                            onCheckedChange={(checked) => setReplaceMode(checked as boolean)}
                          />
                          <div className="flex-1">
                            <Label
                              htmlFor="replace-mode"
                              className="text-sm font-medium cursor-pointer"
                            >
                              Replace entire budget for {preview.year}
                            </Label>
                            <p className="text-xs text-muted-foreground mt-1">
                              {replaceMode
                                ? 'All existing budget data for this year will be replaced with the imported data.'
                                : 'Only the roles in the import file will be updated. Other roles will remain unchanged.'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          {canImport && (
            <Button onClick={handleImport} disabled={isProcessing}>
              {isProcessing ? 'Importing...' : 'Import Budget'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
